import {invariant} from '@raison/toolbox';
import {BaseCurrencyCode, CryptoCurrencyCode, CurrencyCode} from '@raison/types';

export interface Currency {
    code: BaseCurrencyCode;
    decimals: number;
}

export namespace Currency {
    export const coerce = asCurrency;
}

export interface CryptoCurrency {
    code: CryptoCurrencyCode;
    decimals: number;
}

export interface CurrencyTyped extends Currency {
    type: CurrencyType;
}
export interface CryptoCurrencyTyped extends CryptoCurrency {
    type: CryptoCurrencyType;
}

interface CurrencyInfoExtras {
    name: string;
    rateValue: number;
    rateQuote: CurrencyCode;
    icon: string;
}

export interface CurrencyInfo extends CurrencyInfoExtras, CurrencyTyped {}
export interface CryptoCurrencyInfo extends CurrencyInfoExtras, CryptoCurrencyTyped {}

export interface CurrencyTrait {
    currencyCode: BaseCurrencyCode;
    currencyDecimals: number;
}

export interface CryptoCurrencyTrait {
    currencyCode: CryptoCurrencyCode;
    currencyDecimals: number;
}

export type CryptoCurrencyType = (typeof CryptoCurrencyType)[keyof typeof CryptoCurrencyType];
export const CryptoCurrencyType = {
    stablecoin: 'stablecoin',
    crypto: 'crypto',
} as const;

export type CurrencyType = (typeof CurrencyType)[keyof typeof CurrencyType];
export const CurrencyType = {
    ...CryptoCurrencyType,
    fiat: 'fiat',
} as const;

export function asCurrency(currencyLike: CryptoCurrency | CryptoCurrencyTrait): CryptoCurrency;
export function asCurrency(currencyLike: Currency | CurrencyTrait): Currency;
export function asCurrency(currencyLike: Currency | CurrencyTrait): Currency {
    if ('code' in currencyLike && 'decimals' in currencyLike) {
        return {code: currencyLike.code, decimals: currencyLike.decimals};
    } else {
        return {code: currencyLike.currencyCode, decimals: currencyLike.currencyDecimals};
    }
}

type Diff<T, U> = {
    [K in keyof T & keyof U]: T[K] extends U[K] ? never : K;
}[keyof T & keyof U];

type CurrenciesTypesDiff = Diff<CurrencyInfo, CryptoCurrencyInfo>;

export function isOfCryptoType<T extends Pick<CurrencyInfo, CurrenciesTypesDiff>>(
    i: T
): i is T & Pick<CryptoCurrencyInfo, CurrenciesTypesDiff> {
    const cryptoTypes: string[] = Object.values(CryptoCurrencyType);
    return cryptoTypes.includes(i.type);
}

export function findCryptoCurrencyByCode(currencyCode: CryptoCurrencyCode) {
    return <T extends {type: CurrencyType; code: CurrencyCode}>(info: T[]) => {
        const currency = info.filter(isOfCryptoType).find(c => c.code === currencyCode);
        invariant(!!currency, `Currency ${currencyCode} not found in v1/currencies`);
        return currency;
    };
}
